import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Mail, MessageSquare, Building2, FlaskConical, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const FadeUp = ({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const CONTACT_TYPES = [
  { id: "demo", icon: FlaskConical, label: "Request a demo", desc: "See AlphaYou in action with your use case" },
  { id: "sales", icon: Building2, label: "Enterprise sales", desc: "NHS trusts, CROs, and large organisations" },
  { id: "support", icon: MessageSquare, label: "Technical support", desc: "Help with integration or API questions" },
  { id: "other", icon: Mail, label: "General enquiry", desc: "Anything else — we'd love to hear from you" },
];

export default function ContactPage() {
  const [type, setType] = useState("demo");
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate submission
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative section-pad pt-32 pb-16">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="orb orb-cyan w-[400px] h-[400px] -top-20 right-0 opacity-15" />
        <div className="container-ay relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="badge-cyan mb-6 mx-auto w-fit">Get in touch</div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Let's talk about<br />
              <span className="text-gradient-cyan">your use case.</span>
            </h1>
            <p className="text-slate-400 text-xl max-w-xl mx-auto">
              Whether you're a GP, a genomics startup, or an NHS trust — we want to understand your workflow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact form */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="container-ay max-w-4xl">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left: type selector */}
            <div className="lg:col-span-2 space-y-3">
              <FadeUp>
                <p className="text-sm font-semibold text-white mb-4">What can we help with?</p>
                {CONTACT_TYPES.map(({ id, icon: Icon, label, desc }) => (
                  <button
                    key={id}
                    onClick={() => setType(id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                      type === id
                        ? "border-ay-cyan/40 bg-ay-cyan/5 shadow-ay-sm"
                        : "border-ay-border bg-ay-surface hover:border-ay-cyan/20"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${type === id ? "bg-ay-cyan/20" : "bg-ay-surface"}`}>
                        <Icon size={15} className={type === id ? "text-ay-cyan" : "text-ay-muted"} />
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${type === id ? "text-white" : "text-slate-300"}`}>{label}</div>
                        <div className="text-xs text-ay-muted mt-0.5">{desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </FadeUp>

              <FadeUp delay={0.2} className="pt-4">
                <div className="ay-card border border-ay-border p-5">
                  <p className="text-xs font-semibold text-white uppercase tracking-widest mb-3">Direct contact</p>
                  <a href="mailto:hello@alphayou.ai" className="flex items-center gap-2 text-sm text-ay-cyan hover:text-white transition-colors">
                    <Mail size={14} /> hello@alphayou.ai
                  </a>
                  <p className="text-xs text-ay-muted mt-3">We respond within 1 business day.</p>
                </div>
              </FadeUp>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <FadeUp delay={0.1}>
                {submitted ? (
                  <div className="ay-card border border-ay-emerald/30 p-10 text-center">
                    <div className="w-16 h-16 rounded-full bg-ay-emerald/10 border border-ay-emerald/30 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 size={28} className="text-ay-emerald" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-3">Message sent</h3>
                    <p className="text-slate-400 mb-6">
                      Thanks for reaching out. We'll get back to you within 1 business day.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: "", email: "", org: "", message: "" }); }}
                      className="btn-outline text-sm"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="ay-card space-y-5">
                    <h3 className="font-bold text-white text-lg mb-1">
                      {CONTACT_TYPES.find((c) => c.id === type)?.label}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Full name *</label>
                        <input
                          type="text" required className="input"
                          placeholder="Dr Jane Smith"
                          value={form.name} onChange={set("name")}
                        />
                      </div>
                      <div>
                        <label className="label">Work email *</label>
                        <input
                          type="email" required className="input"
                          placeholder="jane@organisation.co.uk"
                          value={form.email} onChange={set("email")}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label">Organisation</label>
                      <input
                        type="text" className="input"
                        placeholder="NHS Trust / Biotech / University"
                        value={form.org} onChange={set("org")}
                      />
                    </div>
                    <div>
                      <label className="label">Message *</label>
                      <textarea
                        required rows={5} className="input resize-none"
                        placeholder={
                          type === "demo" ? "Tell us about your use case and what you'd like to see in a demo..."
                          : type === "sales" ? "Tell us about your organisation, team size, and requirements..."
                          : type === "support" ? "Describe the issue or question you're facing..."
                          : "How can we help?"
                        }
                        value={form.message} onChange={set("message")}
                      />
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                      {loading
                        ? <><Loader2 size={16} className="animate-spin" /> Sending...</>
                        : <>Send message <ArrowRight size={16} /></>
                      }
                    </button>
                    <p className="text-xs text-ay-muted text-center">
                      By submitting, you agree to our Privacy Policy. We never share your data.
                    </p>
                  </form>
                )}
              </FadeUp>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
